import { Injectable } from "@angular/core";
import {
	ActivatedRouteSnapshot,
	RouterStateSnapshot,
	Resolve,
} from "@angular/router";
import { Observable } from "rxjs";
import { LocalStorageService } from "@dodo/local-storage";
import { EnvConfigProvider } from "./env-config.provider";
import { LocalStorageKeys } from "./local-storage-state-restor.service";

@Injectable({
	providedIn: "root",
})
export class QueryParamResolver implements Resolve<any> {
	private sessionStorageKey = "query-params";
	private dmParamsKey = "dm_query_params";

	constructor(
		private localStore: LocalStorageService,
		private envConfigProvider: EnvConfigProvider
	) {}

	resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
		this.saveResellerId(route);
		this.clearProductSelections();
		this.saveProductSelectionsParams(route);
		this.saveSIM(route);
	}

	private clearProductSelections() {
		this.clearFromStorage(LocalStorageKeys.promoDiscountCode);
		this.clearFromStorage(LocalStorageKeys.dataLimit);
		this.clearFromStorage(LocalStorageKeys.contract);
		this.clearFromStorage(LocalStorageKeys.speed);
		this.clearFromStorage(LocalStorageKeys.productType);
		this.clearFromStorage(LocalStorageKeys.gnafId);
	}

	private saveQueryParamInStore(
		route: ActivatedRouteSnapshot,
		paramName: string,
		localStorageKey: string
	) {
		const val = this.getParam(route, paramName);

		if (val) {
			this.localStore.setInSessionStorage(localStorageKey, val);
		}

		if (route.queryParamMap.keys.length > 0) {
			this.localStore.setInSessionStorage(
				this.sessionStorageKey,
				route.queryParams
			);
		}
		this.parseParam(route);
	}

	private saveSIM(route: ActivatedRouteSnapshot) {
		const defaultParamName = "sim";
		const defaultLocalStorageKey = LocalStorageKeys.sim;

		this.saveQueryParamInStore(route, defaultParamName, defaultLocalStorageKey);
	}

	private saveResellerId(route: ActivatedRouteSnapshot) {
		const defaultParamName = "resellerid";
		const defaultLocalStorageKey = LocalStorageKeys.resellerId;

		const paramNameFromEvnConfig = this.envConfigProvider.config
			.resellerIdParamName;

		const paramName = paramNameFromEvnConfig || defaultParamName;

		const localStorageKey = defaultLocalStorageKey;
		this.saveQueryParamInStore(route, paramName, localStorageKey);
	}

	private saveParam(
		route: ActivatedRouteSnapshot,
		defaultParamName: string,
		localStorageKey: string,
		paramNameFromEvnConfig: string
	) {
		const paramName = paramNameFromEvnConfig || defaultParamName;
		this.saveQueryParamInStore(route, paramName, localStorageKey);
	}

	private saveProductSelectionsParams(route: ActivatedRouteSnapshot) {
		this.saveParam(
			route,
			"p_code",
			LocalStorageKeys.promoDiscountCode,
			this.envConfigProvider.config.promoDiscountCodeParamNameMobile
		);
		this.saveParam(
			route,
			"promo_code",
			LocalStorageKeys.promoDiscountCode,
			this.envConfigProvider.config.promoDiscountCodeParamName
		);
		this.saveParam(
			route,
			"data_limit",
			LocalStorageKeys.dataLimit,
			this.envConfigProvider.config.dataLimitParamName
		);
		this.saveParam(
			route,
			"contract",
			LocalStorageKeys.contract,
			this.envConfigProvider.config.contractParamName
		);
		this.saveParam(
			route,
			"speed",
			LocalStorageKeys.speed,
			this.envConfigProvider.config.speedParamName
		);
		this.saveParam(
			route,
			"prod_type",
			LocalStorageKeys.productType,
			this.envConfigProvider.config.productTypeParamName
		);
		this.saveParam(
			route,
			"gnaf_id",
			LocalStorageKeys.gnafId,
			this.envConfigProvider.config.gnafIdParamName
		);
		this.saveParam(
			route,
			"target",
			LocalStorageKeys.target,
			this.envConfigProvider.config.targetParamName
		);
		this.saveParam(
			route,
			"plan",
			LocalStorageKeys.plan,
			this.envConfigProvider.config.plan
		);
	}

	private clearFromStorage(key: string) {
		this.localStore.setInSessionStorage(key, null);
	}

	private getParam(route: ActivatedRouteSnapshot, paramName: string) {
		const paramVal = route.queryParamMap.get(paramName);

		if (!paramVal) {
			if (route.firstChild) {
				return this.getParam(route.firstChild, paramName);
			} else {
				return undefined;
			}
		} else {
			return paramVal;
		}
	}

	private parseParam(route: ActivatedRouteSnapshot) {
		if (route.queryParamMap.keys.indexOf("source") > -1) {
			this.localStore.setInSessionStorage(
				this.dmParamsKey,
				route.queryParams
			);
		}
	}
}
